import React, { useContext, useEffect, useState, useCallback, useRef } from 'react';
import SectionHeader from '../../Elements/SectionHeader';
import Button from '../../Elements/Button';
import { Warehouse, Refrigerator, Archive, Boxes, Box, Trash2, Pencil } from 'lucide-react';
import { UserContext } from '../../../context/UserContext';
import { VENUE_TYPES } from '../../../lib/consts/venueTypes';
import MesasSection from '../ClubEntradasComponents/MesasSection';
import apiFetch, { safeJson, apiErrorDetail } from '../../../context/api';
import { canEditStockLocations } from '../../../lib/helpers/permissions';
import ConfirmDialog from '../../Elements/ConfirmDialog';
import { listaQueSeDesplaza, desbordaVertical } from './gestoLista';

/**
 * Vista aérea del almacén (Opción C): cada ubicación es una caja flotante
 * colocable en el plano. Arrastra la cabecera para moverla; pulsa ➡ en un
 * producto para traspasarlo a otra ubicación (con báscula si es por peso).
 * Backend: GET /v1/stock/board, PATCH/POST /v1/stock/locations, POST /v1/stock/transfer
 */

const KIND_LABELS = { principal: 'Principal', nevera: 'Nevera', armario: 'Armario', sub_almacen: 'Sub-almacén' };
const KIND_ICON = { principal: Warehouse, nevera: Refrigerator, armario: Archive, sub_almacen: Boxes };
const KIND_BADGE = {
  principal: 'bg-indigo-50 text-indigo-700',
  nevera: 'bg-sky-50 text-sky-700',
  armario: 'bg-amber-50 text-amber-700',
  sub_almacen: 'bg-slate-100 text-slate-600',
};
// Pulsación larga que "levanta" un producto en táctil (ver onCardPointerDown).
// 350 ms es lo que usan iOS/Android para reordenar: por debajo salta con un
// roce, por encima se siente colgado.
const MANTENER_MS = 350;
const BOX_W = 230;
const BOX_H = 200; // alto estimado de caja (para fit / límites)

const StockBoard = () => {
  const [token, , , venueID, , , , accessPrivileges, , , , venueType] = useContext(UserContext);
  // El mapa de ubicaciones (crear/renombrar/semáforo/borrar/colocar) es cosa del
  // dueño: el backend responde 403 al staff, así que aquí ni se ofrecen los
  // controles. El staff sigue viendo el tablero y moviendo stock (traspasos).
  const canEditMap = canEditStockLocations(accessPrivileges);
  const [locations, setLocations] = useState([]);
  const [products, setProducts] = useState([]);
  const [positions, setPositions] = useState({}); // { locId: {x,y} }
  const [sizes, setSizes] = useState({}); // { locId: {w,h} }
  const resizeRef = useRef(null); // { id, startX, startY, baseW, baseH }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transfer, setTransfer] = useState(null); // { product, fromLoc }
  const [showAdd, setShowAdd] = useState(false);
  const [zone, setZone] = useState('barra'); // barra | dispensario
  const [boxCat, setBoxCat] = useState({}); // { locId: categoría activa en esa caja }
  const [cardDrag, setCardDrag] = useState(null); // { product, fromLoc, x, y, overId } (ghost)
  const cardDragRef = useRef(null); // { product, fromLoc, startX, startY, active, overId }
  const floorRef = useRef(null);
  const dragRef = useRef(null); // { id, startX, startY, baseX, baseY }
  const [view, setView] = useState({ x: 0, y: 0, z: 1 }); // pan + zoom del lienzo
  const panRef = useRef(null); // { startX, startY, baseX, baseY }
  const pointersRef = useRef(new Map()); // pointerId -> {x,y} (para pinch en tablet)
  const pinchRef = useRef(null); // { startDist, baseZ, baseX, baseY, midX, midY }
  const holdTimerRef = useRef(null); // pulsación larga en curso (táctil)
  const positionsRef = useRef({});
  useEffect(() => { positionsRef.current = positions; }, [positions]);
  const sizesRef = useRef({});
  useEffect(() => { sizesRef.current = sizes; }, [sizes]);

  const cancelarMantener = useCallback(() => {
    if (holdTimerRef.current) { clearTimeout(holdTimerRef.current); holdTimerRef.current = null; }
  }, []);
  useEffect(() => () => cancelarMantener(), [cancelarMantener]);

  // bbox del contenido en coords mundo (usa tamaño real de cada caja)
  const contentBBox = () => {
    const ps = positionsRef.current || {};
    const ids = Object.keys(ps);
    if (ids.length === 0) return null;
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    ids.forEach((id) => {
      const p = ps[id]; const s = sizesRef.current?.[id] || {};
      const w = s.w || BOX_W; const h = (s.h || BOX_H) + 130; // grid + cabecera/pills/asa
      minX = Math.min(minX, p.x); minY = Math.min(minY, p.y);
      maxX = Math.max(maxX, p.x + w); maxY = Math.max(maxY, p.y + h);
    });
    return { minX, minY, maxX, maxY };
  };

  // Paneo libre con margen amplio: deja pasear ~una pantalla más allá del
  // contenido en cualquier dirección, para poder llevar cualquier caja al centro
  // sin perderse al infinito.
  const clampView = useCallback((v) => {
    const el = floorRef.current;
    const bb = contentBBox();
    if (!el || !bb) return v;
    const vpW = el.clientWidth, vpH = el.clientHeight;
    const buf = Math.max(vpW, vpH); // ~una pantalla de margen libre
    const xMin = -buf - bb.maxX * v.z, xMax = vpW + buf - bb.minX * v.z;
    const yMin = -buf - bb.maxY * v.z, yMax = vpH + buf - bb.minY * v.z;
    return { ...v, x: Math.min(xMax, Math.max(xMin, v.x)), y: Math.min(yMax, Math.max(yMin, v.y)) };
  }, []);

  const fitToView = useCallback(() => {
    const el = floorRef.current;
    const bb = contentBBox();
    if (!el || !bb) { setView({ x: 0, y: 0, z: 1 }); return; }
    const pad = 48;
    const cw = Math.max(1, bb.maxX - bb.minX), ch = Math.max(1, bb.maxY - bb.minY);
    const z = Math.min(2.5, Math.max(0.3, Math.min((el.clientWidth - pad * 2) / cw, (el.clientHeight - pad * 2) / ch, 1)));
    setView({ z, x: (el.clientWidth - cw * z) / 2 - bb.minX * z, y: (el.clientHeight - ch * z) / 2 - bb.minY * z });
  }, []);

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) };
  const authGet = { headers: { Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) } };
  // Formato unificado con el resto del software: punto decimal, coma miles (en-GB).
  const fmtN = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 3 });
  const fmtMoney = (n) => Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const unitOf = (p) => (p.unit_type === 'weight' ? 'g' : 'u');

  const fetchBoard = useCallback(async () => {
    if (!venueID) return;
    setLoading(true); setError('');
    try {
      const res = await apiFetch(`/v1/stock/board?venue_id=${venueID}`, authGet);
      const data = await safeJson(res);
      if (!res.ok) throw new Error(apiErrorDetail(data, 'Error al cargar el almacén'));
      setLocations(data.locations || []);
      setProducts(data.products || []);
      // posiciones: usa pos_x/pos_y; auto-layout en fila si null
      setPositions((prev) => {
        const next = { ...prev };
        (data.locations || []).forEach((l, i) => {
          if (next[l.id]) return;
          next[l.id] = (l.pos_x != null && l.pos_y != null) ? { x: l.pos_x, y: l.pos_y } : { x: 24 + i * (BOX_W + 20), y: 24 };
        });
        return next;
      });
      setSizes((prev) => {
        const next = { ...prev };
        (data.locations || []).forEach((l) => {
          if (next[l.id]) return;
          next[l.id] = { w: l.pos_w || 280, h: l.pos_h || 240 };
        });
        return next;
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueID, token]);

  useEffect(() => { fetchBoard(); }, [fetchBoard]);

  // Renombrar ubicación (lapicito en la cabecera de la caja). El Almacén principal
  // también se puede renombrar: TODA la lógica cuelga de kind='principal' (badge),
  // nunca del texto del nombre — verificado en BE y FE antes de permitirlo.
  const [editingLocId, setEditingLocId] = useState(null);
  const [editingName, setEditingName] = useState('');
  // Ubicaciones a la espera de confirmación: vetarla (semáforo a rojo) y borrarla.
  const [pendienteVeto, setPendienteVeto] = useState(null);
  const [pendienteBorrado, setPendienteBorrado] = useState(null);
  const [pendientePrincipal, setPendientePrincipal] = useState(null);
  // La principal de HOY: se nombra en el diálogo de promoción (va a degradarse) y
  // decide si el formulario de alta ofrece siquiera el tipo «principal».
  const principalActual = locations.find((l) => l.kind === 'principal') || null;

  /** Lo que hay que resaltar antes de promover `loc`, o null si no hay nada que avisar.
   *
   *  Lo segundo es lo importante: promover una nevera QUE TIENE GÉNERO la saca del pool
   *  de ventas de sala, y en un club con «sala estricta» un producto que solo viva ahí
   *  deja de poder venderse. Recomendar un Almacén principal nuevo y vacío no basta si
   *  la pantalla no dice cuándo el elegido no lo está. Cuenta las dos zonas (barra y
   *  dispensario), no solo la que se está mirando: el cambio afecta a las dos. */
  const notaDePromocion = (loc) => {
    const conStock = products.filter(
      (p) => Number((p.available_by_location ?? p.by_location)?.[loc.id] || 0) !== 0
    ).length;
    const avisos = [];
    if (conStock > 0) {
      avisos.push(
        `«${loc.name}» tiene ${conStock} ${conStock === 1 ? 'producto' : 'productos'} con stock. ` +
        'Ese género dejará de venderse desde aquí. Si solo está en esta ubicación, revisa el POS antes de confirmar.'
      );
    }
    if (principalActual) {
      avisos.push(
        `«${principalActual.name}» pasará a Sub-almacén: dejará de ser el destino por defecto y volverá al pool de ventas.`
      );
    }
    return avisos.length ? avisos.join(' ') : null;
  };
  const saveRename = async (loc) => {
    const name = editingName.trim();
    setEditingLocId(null);
    if (!name || name === loc.name) return;
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'PATCH', headers, body: JSON.stringify({ name }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo renombrar la ubicación'));
      await fetchBoard();
    } catch (e) {
      setError(e.message || 'No se pudo renombrar la ubicación');
    }
  };

  // Semáforo dispensable: rojo = las ventas nunca salen de aquí (almacén externo/
  // frío, caso Kong). Solo afecta a ventas; entradas y traspasos siguen igual.
  //
  // Vetar una ubicación se pregunta con el diálogo de la app, no con `window.confirm`: en el
  // iPad del club ese diálogo se traga y devuelve `false` en silencio, así que el semáforo
  // solo fallaba EN UN SENTIDO —dejaba poner verde y no rojo— sin decir por qué
  // (reporte CRUO 24-08). Poner en verde no pregunta: no se lleva nada por delante.
  const toggleDispensable = async (loc) => {
    const next = !(loc.dispensable ?? true);
    if (!next) {
      setPendienteVeto(loc);
      return;
    }
    await aplicarDispensable(loc, next);
  };

  const aplicarDispensable = async (loc, next) => {
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'PATCH', headers, body: JSON.stringify({ dispensable: next }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo cambiar el estado'));
      await fetchBoard();
    } catch (e) {
      setError(e.message || 'No se pudo cambiar el estado de la ubicación');
    }
  };

  // Marcar una ubicación como Almacén principal. NO mueve ni un gramo: solo cambia
  // dónde cae el género NUEVO (`resolve_principal_location_id`) y saca la ubicación
  // del pool de venta de sala. Como son cuatro efectos y ninguno se ve al instante,
  // se preguntan enteros en el diálogo en vez de con un «¿seguro?».
  const aplicarPrincipal = async (loc) => {
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, {
        method: 'PATCH', headers, body: JSON.stringify({ kind: 'principal' }),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo marcar como principal'));
      await fetchBoard();
    } catch (e) {
      setError(e.message || 'No se pudo marcar como principal');
    }
  };

  // Borrar una ubicación (su stock vuelve a la principal). La principal no se borra.
  const handleDeleteLocation = (loc) => {
    if (loc.kind === 'principal') return;
    setPendienteBorrado(loc);
  };

  const borrarUbicacion = async (loc) => {
    try {
      const res = await apiFetch(`/v1/stock/locations/${loc.id}?venue_id=${venueID}`, { method: 'DELETE', headers });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'No se pudo borrar la ubicación'));
      await fetchBoard();
    } catch (e) {
      setError(e.message || 'No se pudo borrar la ubicación');
    }
  };

  // ── Drag de cajas (pointer events, táctil). Delta / zoom = mundo. ──
  const onPointerDownBox = (e, loc) => {
    if (!canEditMap) return;   // colocar cajas también es editar el mapa
    if (e.target.closest('[data-no-drag]')) return;
    e.stopPropagation(); // no iniciar pan del lienzo
    const pos = positions[loc.id] || { x: 24, y: 24 };
    dragRef.current = { id: loc.id, startX: e.clientX, startY: e.clientY, baseX: pos.x, baseY: pos.y };
  };
  // ── Pan del lienzo (arrastrar el fondo) ──
  const onFloorPointerDown = (e) => {
    // Registrar el puntero para detectar pinch (2 dedos en tablet).
    pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointersRef.current.size === 2) {
      const [a, b] = [...pointersRef.current.values()];
      pinchRef.current = {
        startDist: Math.hypot(a.x - b.x, a.y - b.y) || 1,
        baseZ: view.z, baseX: view.x, baseY: view.y,
        midX: (a.x + b.x) / 2, midY: (a.y + b.y) / 2,
      };
      panRef.current = null; dragRef.current = null; resizeRef.current = null; cardDragRef.current = null;
      return;
    }
    if (e.target.closest('[data-box]')) return; // si pulsas una caja, no paneo
    panRef.current = { startX: e.clientX, startY: e.clientY, baseX: view.x, baseY: view.y };
  };
  const onResizePointerDown = (e, loc) => {
    if (!canEditMap) return;
    e.stopPropagation();
    const s = sizes[loc.id] || { w: 280, h: 240 };
    resizeRef.current = { id: loc.id, startX: e.clientX, startY: e.clientY, baseW: s.w, baseH: s.h };
  };
  // ── Drag de PRODUCTO entre cajas (tap = abrir diálogo; arrastrar = soltar en otra caja) ──
  const onCardPointerDown = (e, product, loc) => {
    e.stopPropagation();
    // Guardar la lista scrollable de la caja para poder hacer scroll manual en tablet
    // (el touch-action:none del board impide el scroll nativo; lo movemos a mano).
    // ...pero solo cuando ese apaño hace falta y tiene efecto (ver gestoLista.js):
    // con ratón, o con una lista que no desborda, la vertical es arrastre.
    const scrollEl = listaQueSeDesplaza(e.currentTarget.closest('[data-scroll]'), e.pointerType);
    cardDragRef.current = {
      product, fromLoc: loc, startX: e.clientX, startY: e.clientY,
      tactil: e.pointerType !== 'mouse',
      active: false, overId: null, mode: null,
      scrollEl, baseScrollTop: scrollEl ? scrollEl.scrollTop : 0,
    };
    cancelarMantener();
    // Cuando la lista se queda la vertical (dedo + lista que desborda), la única
    // forma de sacar un producto hacia ABAJO es levantarlo antes: se mantiene
    // pulsado y a partir de ahí se arrastra en cualquier dirección. Es el gesto
    // de reordenar de iOS/Android, así que no hay que explicarlo. El toque corto
    // sigue abriendo el diálogo y el deslizamiento sigue desplazando la lista.
    if (!scrollEl) return;
    holdTimerRef.current = setTimeout(() => {
      const cd = cardDragRef.current;
      if (!cd || cd.mode !== null) return;   // el dedo ya se movió: era swipe, no pulsación
      cd.mode = 'drag';
      if (typeof navigator !== 'undefined') navigator.vibrate?.(10);
      // El fantasma bajo el dedo ES el acuse de recibo: sin él no hay forma de
      // saber que el producto ya está levantado.
      setCardDrag({ product: cd.product, fromLoc: cd.fromLoc, x: cd.startX, y: cd.startY, overId: null, tactil: cd.tactil });
    }, MANTENER_MS);
  };
  const onPointerMove = (e) => {
    // Pinch (2 dedos): zoom proporcional a la separación, anclado al punto medio.
    if (pointersRef.current.has(e.pointerId)) pointersRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    const pinch = pinchRef.current;
    if (pinch && pointersRef.current.size >= 2) {
      const [a, b] = [...pointersRef.current.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y) || 1;
      const ratio = dist / pinch.startDist;
      const rect = floorRef.current?.getBoundingClientRect();
      const cx = pinch.midX - (rect?.left || 0), cy = pinch.midY - (rect?.top || 0);
      setView(() => {
        const z = Math.min(2.5, Math.max(0.3, pinch.baseZ * ratio));
        const wx = (cx - pinch.baseX) / pinch.baseZ, wy = (cy - pinch.baseY) / pinch.baseZ;
        return clampView({ z, x: cx - wx * z, y: cy - wy * z });
      });
      return;
    }
    const cd = cardDragRef.current;
    if (cd) {
      const dx = e.clientX - cd.startX;
      const dy = e.clientY - cd.startY;
      if (cd.mode === null) {
        if (Math.hypot(dx, dy) < 5) return;   // umbral: por debajo es un tap
        // El dedo se ha movido antes de tiempo: era un deslizamiento, no una
        // pulsación larga. Se descarta el temporizador y se decide como siempre —
        // vertical → scroll de la lista de la caja; horizontal → traspaso.
        cancelarMantener();
        cd.mode = (cd.scrollEl && Math.abs(dy) > Math.abs(dx)) ? 'scroll' : 'drag';
      }
      if (cd.mode === 'scroll') {
        cd.scrollEl.scrollTop = cd.baseScrollTop - dy;
        return;
      }
      cd.active = true;
      const box = document.elementFromPoint(e.clientX, e.clientY)?.closest('[data-loc-id]');
      cd.overId = box ? box.getAttribute('data-loc-id') : null;
      setCardDrag({ product: cd.product, fromLoc: cd.fromLoc, x: e.clientX, y: e.clientY, overId: cd.overId, tactil: cd.tactil });
      return;
    }
    const rz = resizeRef.current;
    if (rz) {
      const w = Math.max(200, rz.baseW + (e.clientX - rz.startX) / view.z);
      const h = Math.max(140, rz.baseH + (e.clientY - rz.startY) / view.z);
      setSizes((s) => ({ ...s, [rz.id]: { w, h } }));
      return;
    }
    const d = dragRef.current;
    if (d) {
      const x = d.baseX + (e.clientX - d.startX) / view.z;
      const y = d.baseY + (e.clientY - d.startY) / view.z;
      setPositions((p) => ({ ...p, [d.id]: { x, y } }));
      return;
    }
    const pan = panRef.current;
    if (pan) {
      setView((v) => clampView({ ...v, x: pan.baseX + (e.clientX - pan.startX), y: pan.baseY + (e.clientY - pan.startY) }));
    }
  };
  const onPointerUp = async (e) => {
    // Soltar un dedo: quitarlo del registro y cerrar el pinch si quedan <2.
    if (e && e.pointerId != null) pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    cancelarMantener();
    const cd = cardDragRef.current;
    if (cd) {
      cardDragRef.current = null;
      setCardDrag(null);
      if (cd.mode === 'scroll') return;                                // fue scroll de la lista
      const { active, overId, product, fromLoc } = cd;
      if (!active) { setTransfer({ product, fromLoc }); return; }       // tap → diálogo
      if (overId && overId !== fromLoc.id) {                            // soltado en otra caja
        setTransfer({ product, fromLoc, presetTo: overId });
      }
      return;
    }
    const rz = resizeRef.current;
    if (rz) {
      resizeRef.current = null;
      const s = sizes[rz.id];
      if (s) {
        try {
          await apiFetch(`/v1/stock/locations/${rz.id}?venue_id=${venueID}`, {
            method: 'PATCH', headers, body: JSON.stringify({ pos_w: Math.round(s.w), pos_h: Math.round(s.h) }),
          });
        } catch { /* tamaño no crítico */ }
      }
      return;
    }
    const d = dragRef.current;
    if (d) {
      dragRef.current = null;
      const pos = positions[d.id];
      if (pos) {
        try {
          await apiFetch(`/v1/stock/locations/${d.id}?venue_id=${venueID}`, {
            method: 'PATCH', headers, body: JSON.stringify({ pos_x: Math.round(pos.x), pos_y: Math.round(pos.y) }),
          });
        } catch { /* posición no crítica */ }
      }
      return;
    }
    panRef.current = null;
  };
  // Cancelación del puntero (p.ej. el navegador toma el gesto): limpiar estado.
  const onPointerCancel = (e) => {
    cancelarMantener();
    if (e && e.pointerId != null) pointersRef.current.delete(e.pointerId);
    if (pointersRef.current.size < 2) pinchRef.current = null;
    cardDragRef.current = null; dragRef.current = null; resizeRef.current = null; panRef.current = null;
    setCardDrag(null);
  };
  // ── Zoom al cursor (rueda / trackpad) ──
  const applyZoom = useCallback((deltaY, clientX, clientY) => {
    const rect = floorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const cx = clientX - rect.left, cy = clientY - rect.top;
    setView((v) => {
      const z = Math.min(2.5, Math.max(0.3, v.z * (deltaY < 0 ? 1.1 : 1 / 1.1)));
      const wx = (cx - v.x) / v.z, wy = (cy - v.y) / v.z; // punto mundo bajo el cursor
      return clampView({ z, x: cx - wx * z, y: cy - wy * z }); // anclar ese punto + límites
    });
  }, [clampView]);
  // wheel con listener no-pasivo (para poder preventDefault). Distingue ratón vs trackpad:
  // - ctrlKey → PINCH del trackpad (o ctrl+rueda) → zoom al cursor.
  // - rueda de ratón (deltaY grande y discreto, sin deltaX) → zoom (comportamiento de siempre).
  // - trackpad dos dedos (deltas pequeños y/o con deltaX) → PAN del lienzo.
  //   Si el gesto cae sobre la lista de una caja que puede scrollear, se deja el scroll nativo.
  // La tablet no pasa por aquí (usa pointer events / pinchRef), así que no se toca.
  useEffect(() => {
    const el = floorRef.current;
    if (!el) return;
    const handler = (e) => {
      if (e.ctrlKey) { // pinch trackpad / ctrl+rueda → zoom
        e.preventDefault();
        applyZoom(e.deltaY, e.clientX, e.clientY);
        return;
      }
      // Sobre una lista que DESBORDA manda la lista, venga rueda o trackpad: se
      // deja el scroll nativo. Antes la rueda de ratón zoomeaba también aquí, y
      // desde que el arrastre vertical mueve el producto (gestoLista.js) eso
      // dejaba al ratón sin ninguna forma de ver los productos de más abajo.
      const list = e.target?.closest?.('[data-scroll]');
      if (desbordaVertical(list)) return;
      const isMouseWheel = e.deltaX === 0 && Math.abs(e.deltaY) >= 40;
      if (isMouseWheel) { // rueda de ratón fuera de una lista → zoom (igual que antes)
        e.preventDefault();
        applyZoom(e.deltaY, e.clientX, e.clientY);
        return;
      }
      // Trackpad dos dedos fuera de una lista: PAN del lienzo.
      e.preventDefault();
      setView((v) => clampView({ ...v, x: v.x - e.deltaX, y: v.y - e.deltaY }));
    };
    el.addEventListener('wheel', handler, { passive: false });
    return () => el.removeEventListener('wheel', handler);
  }, [applyZoom, clampView]);

  const addLocation = async (name, kind) => {
    setError('');
    try {
      const res = await apiFetch('/v1/stock/locations', { method: 'POST', headers, body: JSON.stringify({ venue_id: venueID, name, kind, is_sales_source: false }) });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error al crear'));
      setShowAdd(false);
      await fetchBoard();
    } catch (e) { setError(e.message); }
  };

  // productos de la zona activa (Barra = no dispensario)
  const inZone = (p) => (zone === 'dispensario' ? !!p.is_dispensario : !p.is_dispensario);
  const catOf = (p) => p.category || 'Sin categoría';
  // productos de la zona presentes en una ubicación (con stock ≠ 0, incluidos NEGATIVOS).
  // Los negativos se muestran (en rojo) para que el staff sepa qué hay que reponer:
  // staff sin estrella puede vender hasta negativo pero no rellenar.
  // Mostramos DISPONIBLE (físico − carritos abiertos): al añadir al carrito el producto ya
  // sale de la balda, así que Ubicaciones refleja lo que queda. Fallback al físico si el BE
  // aún no envía available_by_location.
  const productsIn = (locId) => products
    .filter(inZone)
    .map((p) => ({ p, qty: Number((p.available_by_location ?? p.by_location)?.[locId] || 0) }))
    .filter((x) => x.qty !== 0)
    .sort((a, b) => {
      const an = a.qty < 0, bn = b.qty < 0;
      if (an !== bn) return an ? -1 : 1;   // negativos primero
      return a.p.name.localeCompare(b.p.name);
    });

  // Todas las posiciones en negativo de la zona activa (para el aviso superior).
  const locName = {};
  locations.forEach((l) => { locName[l.id] = l.name; });
  const negatives = [];
  products.filter(inZone).forEach((p) => {
    Object.entries(p.available_by_location ?? p.by_location ?? {}).forEach(([locId, q]) => {
      const qn = Number(q || 0);
      if (qn < 0) negatives.push({ p, locId, qty: qn });
    });
  });
  negatives.sort((a, b) => a.qty - b.qty);

  return (
    <div className="p-4 sm:p-6">
      {/* Club social (CRUO): las mesas se gestionan aquí, junto al resto de ubicaciones.
          Entidad aparte (service_tables) — el board de stock ni se entera. */}
      {venueType === VENUE_TYPES.SOCIAL_CLUB && (
        <div className="mb-4">
          <MesasSection />
        </div>
      )}
      <SectionHeader
        title="Ubicaciones"
        subtitle="Vista aérea · arrastra el fondo para mover, rueda para zoom, ⠿ para mover una caja. Toca un producto para traspasarlo, o mantenlo pulsado para arrastrarlo a otra."
        sticky={false}
        className="mb-3"
      >
        {canEditMap && <Button onClick={() => setShowAdd(true)}>+ Ubicación</Button>}
      </SectionHeader>

      {/* Pestañas de zona (Barra / Dispensario) */}
      <div className="mb-4 inline-flex gap-1 rounded-xl bg-[var(--muted)] p-1">
        {(venueType === VENUE_TYPES.SOCIAL_CLUB
          ? [['barra', 'Barra']]
          : [['barra', 'Barra'], ['dispensario', 'Dispensario']]
        ).map(([k, label]) => (
          <button
            key={k}
            onClick={() => setZone(k)}
            className={`rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${zone === k ? 'bg-white text-[var(--foreground)] shadow-sm' : 'text-[var(--muted-foreground)] hover:text-[var(--foreground)]'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {error && <p className="mb-3 rounded-md bg-red-50 px-3 py-2 text-sm text-[var(--destructive)]">{error}</p>}
      {loading && <p className="mb-3 text-sm text-[var(--muted-foreground)]">Cargando…</p>}

      {/* Aviso de stock en negativo (vendido por debajo de 0): pendiente de reponer. */}
      {negatives.length > 0 && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-[var(--destructive)]">
            ⚠️ {negatives.length} {negatives.length === 1 ? 'producto en negativo' : 'productos en negativo'} · pendientes de reponer
          </div>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {negatives.map(({ p, locId, qty }) => (
              <span key={`${p.id}-${locId}`} className="inline-flex items-center gap-1 rounded-md border border-red-200 bg-white px-2 py-0.5 text-[11px] text-[var(--foreground)]">
                <span className="font-medium">{p.name}</span>
                <span className="text-[var(--muted-foreground)]">· {locName[locId] || 'Ubicación'}</span>
                <span className="font-bold tabular-nums text-[var(--destructive)]">{fmtN(qty)}{unitOf(p)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      <div
        ref={floorRef}
        onPointerDown={onFloorPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onPointerCancel={onPointerCancel}
        className="relative w-full select-none overflow-hidden rounded-2xl border-2 border-dashed border-[var(--border)]"
        style={{
          height: 'calc(100vh - 280px)', minHeight: 420,
          cursor: panRef.current ? 'grabbing' : 'grab', touchAction: 'none',
          backgroundImage: 'linear-gradient(var(--border) 1px, transparent 1px), linear-gradient(90deg, var(--border) 1px, transparent 1px)',
          backgroundSize: `${28 * view.z}px ${28 * view.z}px`,
          backgroundPosition: `${view.x}px ${view.y}px`,
        }}
      >
        {/* Capa "mundo": pan + zoom. Las cajas viven aquí en coords mundo. */}
        <div style={{ position: 'absolute', inset: 0, transform: `translate(${view.x}px, ${view.y}px) scale(${view.z})`, transformOrigin: '0 0' }}>
        {locations.map((loc) => {
          const pos = positions[loc.id] || { x: 24, y: 24 };
          const size = sizes[loc.id] || { w: 280, h: 240 };
          const items = productsIn(loc.id);
          const sumQty = items.reduce((s, { qty }) => s + qty, 0);
          const value = items.reduce((s, { p, qty }) => s + qty * (Number(p.price) || 0), 0);
          const boxCats = Array.from(new Set(items.map(({ p }) => catOf(p)))).sort((a, b) => a.localeCompare(b));
          const activeCat = boxCat[loc.id] || 'all';
          const shown = activeCat === 'all' ? items : items.filter(({ p }) => catOf(p) === activeCat);
          return (
            <div
              key={loc.id}
              data-box
              data-loc-id={loc.id}
              className={`absolute rounded-xl border bg-white shadow-md border-[var(--border)] ${cardDrag && cardDrag.overId === loc.id && cardDrag.fromLoc.id !== loc.id ? 'ring-2 ring-[var(--primary)] ring-offset-1' : ''}`}
              style={{ left: pos.x, top: pos.y, width: size.w, cursor: 'default' }}
            >
              {/* Asa de arrastre (mover la caja) */}
              <div
                onPointerDown={(e) => onPointerDownBox(e, loc)}
                className="flex items-center justify-center rounded-t-xl bg-[var(--muted)] py-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]"
                style={{ cursor: 'default', touchAction: 'none' }}
                title="Arrastrar para mover"
              >
                <span className="text-base leading-none tracking-widest select-none">⠿</span>
              </div>
              <div className="p-3">
                <div className="mb-2 flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex min-w-0 items-center gap-1.5">
                      {(() => { const KindIcon = KIND_ICON[loc.kind] || Box; return <KindIcon size={15} className="flex-shrink-0 text-[var(--muted-foreground)]" />; })()}
                      {editingLocId === loc.id ? (
                        <input
                          data-no-drag
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onPointerDown={(e) => e.stopPropagation()}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') saveRename(loc);
                            if (e.key === 'Escape') setEditingLocId(null);
                          }}
                          onBlur={() => saveRename(loc)}
                          className="w-full min-w-0 rounded border border-[var(--border-hover)] px-1.5 py-0.5 text-sm font-semibold text-[var(--foreground)] focus:border-[var(--primary)] focus:outline-none"
                          aria-label="Nombre de la ubicación"
                        />
                      ) : (
                        <>
                          <div className="truncate text-sm font-semibold text-[var(--foreground)]">{loc.name}</div>
                          {canEditMap && (
                          <button
                            data-no-drag
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); setEditingLocId(loc.id); setEditingName(loc.name); }}
                            title="Renombrar ubicación"
                            className="relative flex-shrink-0 rounded-md p-0.5 text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-colors"
                          >
                            {/* Mismo problema que el semáforo: 13 px de lápiz no se tocan con el
                                dedo. Crece hacia arriba y abajo, que es donde no estorba. */}
                            <span className="absolute -inset-y-4 -inset-x-1" aria-hidden />
                            <Pencil size={13} />
                          </button>
                          )}
                          {canEditMap && loc.kind !== 'principal' && (
                            <button
                              data-no-drag
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); toggleDispensable(loc); }}
                              title={(loc.dispensable ?? true)
                                ? 'Dispensable: las ventas pueden salir de aquí. Clic para vetarla.'
                                : 'NO dispensable: las ventas nunca salen de aquí. Clic para permitirla.'}
                              aria-label={`Semáforo de ${loc.name}`}
                              className="relative flex-shrink-0 rounded-md p-0.5 hover:bg-[var(--muted)] transition-colors"
                            >
                              {/* El punto sigue midiendo 12 px, pero lo PULSABLE crece hasta los
                                  44 de alto que pide un dedo. Antes el botón entero medía ~16 px
                                  y en el iPad no había forma de acertarle (reporte CRUO 24-08).
                                  Va por capa absoluta y no por padding para no cambiar el alto de
                                  la cabecera, y se estira hacia la derecha —donde no hay nada—
                                  en vez de hacia el lápiz, que es igual de pequeño. */}
                              <span className="absolute -inset-y-4 -left-1.5 -right-3" aria-hidden />
                              <span className={`block h-3 w-3 rounded-full ${(loc.dispensable ?? true) ? 'bg-green-500' : 'bg-red-500'}`} />
                            </button>
                          )}
                          {/* Una ubicación vetada para ventas NO puede ser principal: el
                              backend responde 400. Aquí ni se ofrece el botón, en vez de
                              dejar que el usuario confirme y se coma el error — misma regla
                              que con el staff y los controles del mapa. */}
                          {canEditMap && loc.kind !== 'principal' && (loc.dispensable ?? true) && (
                            <button
                              data-no-drag
                              onPointerDown={(e) => e.stopPropagation()}
                              onClick={(e) => { e.stopPropagation(); setPendientePrincipal(loc); }}
                              title="Marcar como Almacén principal: el género nuevo entrará aquí por defecto"
                              aria-label={`Marcar «${loc.name}» como Almacén principal`}
                              className="relative flex-shrink-0 rounded-md p-0.5 text-[var(--muted-foreground)] hover:bg-[var(--muted)] hover:text-[var(--foreground)] transition-colors"
                            >
                              {/* Misma zona táctil ampliada que el lápiz y el semáforo: 13 px
                                  no se tocan con el dedo en el iPad del club (reporte CRUO 24-08). */}
                              <span className="absolute -inset-y-4 -inset-x-1" aria-hidden />
                              <Warehouse size={13} />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${KIND_BADGE[loc.kind] || KIND_BADGE.sub_almacen}`}>{KIND_LABELS[loc.kind] || loc.kind}</span>
                      {(loc.dispensable ?? true) === false && (
                        <span className="inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-red-100 text-red-700">No dispensable</span>
                      )}
                    </div>
                    <div className="mt-1 text-[11px] text-[var(--muted-foreground)]">{items.length} prod · {fmtN(sumQty)} ud · €{fmtMoney(value)}</div>
                  </div>
                  {canEditMap && loc.kind !== 'principal' && (
                    <button
                      data-no-drag
                      onPointerDown={(e) => e.stopPropagation()}
                      onClick={(e) => { e.stopPropagation(); handleDeleteLocation(loc); }}
                      title="Borrar ubicación (el stock vuelve a la principal)"
                      className="flex-shrink-0 rounded-md p-1 text-[var(--muted-foreground)] hover:bg-red-50 hover:text-red-600 transition-colors"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
                {/* Pills de categoría (solo las presentes en esta caja). Wrap si no caben. */}
                {boxCats.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1" data-no-drag>
                    <button onClick={() => setBoxCat((m) => ({ ...m, [loc.id]: 'all' }))} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold transition-colors ${activeCat === 'all' ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--border)]'}`}>Todas</button>
                    {boxCats.map((c) => (
                      <button key={c} onClick={() => setBoxCat((m) => ({ ...m, [loc.id]: c }))} className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-colors ${activeCat === c ? 'bg-[var(--primary)] text-[var(--primary-foreground)]' : 'bg-[var(--muted)] text-[var(--muted-foreground)] hover:bg-[var(--border)]'}`}>{c}</button>
                    ))}
                  </div>
                )}
                <div
                  className="grid gap-1.5 overflow-y-auto"
                  data-no-drag
                  data-scroll
                  style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(118px, 1fr))', maxHeight: size.h }}
                >
                  {shown.length === 0 && <p className="col-span-full py-2 text-center text-xs text-[var(--muted-foreground)]">Vacío</p>}
                  {shown.map(({ p, qty }) => {
                    const neg = qty < 0;                                 // en negativo: pendiente de reponer
                    const low = !neg && !p.is_dispensario && qty <= 5;   // umbral simple (Barra)
                    // El producto que se está llevando se marca EN SU SITIO. El
                    // fantasma que sigue al dedo es una capa fija y el iPad no la
                    // repinta durante el gesto; esto es contenido normal, se pinta
                    // siempre, y además dice de dónde salió lo que llevas cogido.
                    const levantado = cardDrag?.product?.id === p.id;
                    return (
                      <div
                        key={p.id}
                        data-no-drag
                        data-levantado={levantado ? 'si' : undefined}
                        onPointerDown={(e) => onCardPointerDown(e, p, loc)}
                        className={`rounded-lg border p-2 transition-transform hover:bg-white ${neg ? 'border-red-200 bg-red-50 hover:border-red-300' : 'border-[var(--border)] bg-[var(--surface-subtle)] hover:border-[var(--primary)]'} ${levantado ? 'scale-105 border-[var(--primary)] opacity-60 shadow-md ring-2 ring-[var(--primary)]' : ''}`}
                        // iOS: al mantener pulsado, Safari saca su menú de selección/copia y
                        // MATA el gesto con un pointercancel — el producto no llegaba a
                        // levantarse nunca en el iPad. Estas tres son las que lo apagan.
                        // Confirmado en un iPad: sin ellas no se levantaba; con ellas, sí.
                        style={{
                          cursor: 'default', touchAction: 'none',
                          WebkitTouchCallout: 'none', WebkitUserSelect: 'none', userSelect: 'none',
                        }}
                        title={neg ? `${p.name} · en negativo, pendiente de reponer` : 'Toca para traspasar · arrástralo a otra caja (con el dedo, manteniéndolo pulsado antes)'}
                      >
                        <div className="truncate text-[11px] font-semibold text-[var(--foreground)]" title={p.name}>{p.name}</div>
                        <div className="mt-0.5 flex items-center gap-1">
                          <span className={`text-sm font-bold tabular-nums ${neg ? 'text-[var(--destructive)]' : low ? 'text-amber-600' : 'text-[var(--foreground)]'}`}>{fmtN(qty)}{unitOf(p)}</span>
                          {neg && <span className="rounded bg-red-100 px-1 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[var(--destructive)]">Reponer</span>}
                        </div>
                        <div className="mt-0.5 text-[9px] text-[var(--muted-foreground)]">tot {fmtN(p.total)}{unitOf(p)}{p.price != null ? ` · ${fmtMoney(p.price)}€` : ''}</div>
                      </div>
                    );
                  })}
                </div>
              </div>{/* /p-3 */}
              {/* Asa de redimensionar */}
              <div
                data-no-drag
                onPointerDown={(e) => onResizePointerDown(e, loc)}
                className="absolute bottom-0.5 right-0.5 select-none text-[var(--muted-foreground)] hover:text-[var(--muted-foreground)]"
                style={{ cursor: 'nwse-resize', touchAction: 'none', lineHeight: 1 }}
                title="Redimensionar"
              >◢</div>
            </div>
          );
        })}
        </div>{/* /capa mundo */}

        {locations.length === 0 && !loading && (
          <p className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-sm text-[var(--muted-foreground)]">{canEditMap ? 'Sin ubicaciones. Crea una con "+ Ubicación".' : 'Sin ubicaciones. Pide al propietario que cree las del club.'}</p>
        )}

        {/* Controles de zoom */}
        <div className="absolute bottom-3 right-3 flex items-center gap-1 rounded-lg border border-[var(--border)] bg-white/90 p-1 shadow-sm backdrop-blur" data-no-drag>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => applyZoom(1, floorRef.current.getBoundingClientRect().left + floorRef.current.clientWidth / 2, floorRef.current.getBoundingClientRect().top + floorRef.current.clientHeight / 2)} className="h-7 w-7 rounded text-[var(--muted-foreground)] hover:bg-[var(--muted)]" title="Alejar">−</button>
          <span className="min-w-[44px] text-center text-xs font-semibold tabular-nums text-[var(--muted-foreground)]">{Math.round(view.z * 100)}%</span>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => applyZoom(-1, floorRef.current.getBoundingClientRect().left + floorRef.current.clientWidth / 2, floorRef.current.getBoundingClientRect().top + floorRef.current.clientHeight / 2)} className="h-7 w-7 rounded text-[var(--muted-foreground)] hover:bg-[var(--muted)]" title="Acercar">+</button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={fitToView} className="ml-1 rounded px-2 py-1 text-xs font-medium text-[var(--muted-foreground)] hover:bg-[var(--muted)]" title="Encajar todo en la vista">Encajar</button>
          <button onPointerDown={(e) => e.stopPropagation()} onClick={() => setView({ x: 0, y: 0, z: 1 })} className="rounded px-2 py-1 text-xs font-medium text-[var(--muted-foreground)] hover:bg-[var(--muted)]" title="Restablecer">Reset</button>
        </div>
      </div>

      {transfer && (
        <TransferDialog
          transfer={transfer}
          locations={locations}
          token={token}
          venueID={venueID}
          onClose={() => setTransfer(null)}
          onDone={() => { setTransfer(null); fetchBoard(); }}
        />
      )}
      {showAdd && (
        <AddLocationDialog
          onClose={() => setShowAdd(false)}
          onCreate={addLocation}
          // Solo se ofrece crear una principal si el club NO tiene ninguna — es el
          // camino recomendado para un club como CRUO (una nueva y vacía no mueve
          // nada), y evita ofrecerle una segunda a quien ya la tiene.
          permitirPrincipal={!principalActual}
        />
      )}

      {/* Ghost que sigue al cursor al arrastrar un producto */}
      {cardDrag && cardDrag.x != null && (
        <div
          data-testid="fantasma-arrastre"
          className="pointer-events-none fixed left-0 top-0 z-[60] rounded-sm border border-[var(--primary)] bg-[var(--card)] px-2 py-1 text-xs font-semibold text-[var(--foreground)] shadow-lg"
          // Posición por `transform`, no por left/top: iOS no repinta un elemento
          // `fixed` mientras hay un gesto táctil en curso, así que en el iPad el
          // traspaso funcionaba pero no se veía NADA moverse — y al levantar el
          // dedo ya lo habíamos quitado (reporte del usuario en tablet, 19-ago).
          // translate3d lo sube a su propia capa y lo mueve el compositor, que sí
          // corre durante el gesto.
          //
          // Abajo-derecha del puntero es lo normal con ratón, pero con el dedo eso
          // es justo donde está la mano: quedaba medio tapado mientras arrastras
          // (reporte en tablet, 19-ago). Con el dedo va ARRIBA, despejado.
          style={{
            transform: cardDrag.tactil
              ? `translate3d(${cardDrag.x + 12}px, ${cardDrag.y - 52}px, 0)`
              : `translate3d(${cardDrag.x + 10}px, ${cardDrag.y + 10}px, 0)`,
            willChange: 'transform',
          }}
        >
          {cardDrag.product.name} →
        </div>
      )}

      {/* Vetar una ubicación y borrarla se preguntan con el diálogo de la app: en tablet,
          `window.confirm` se traga y las dos acciones se quedaban muertas sin decir nada. */}
      <ConfirmDialog
        active={!!pendienteVeto}
        title={`¿Vetar «${pendienteVeto?.name || ''}» para las ventas?`}
        description="Las ventas dejarán de salir de aquí y caerán al Almacén principal. Entradas y traspasos no cambian."
        confirmLabel="Vetar para ventas"
        onConfirm={() => {
          const loc = pendienteVeto;
          setPendienteVeto(null);
          if (loc) aplicarDispensable(loc, false);
        }}
        onCancel={() => setPendienteVeto(null)}
      />

      {/* Cuatro cosas cambian al promover y ninguna se ve al instante, así que se
          dicen enteras. Lo resaltado (`note`) es lo que toca a OTRA cosa: la principal
          actual que se degrada, y —lo más fácil de no ver— que la ubicación elegida
          tiene género dentro y va a salir del pool de ventas de sala. */}
      <ConfirmDialog
        active={!!pendientePrincipal}
        title={`¿Marcar «${pendientePrincipal?.name || ''}» como Almacén principal?`}
        description="El género nuevo entrará aquí por defecto. No se mueve nada de sitio. Las ventas dejarán de salir de aquí."
        note={pendientePrincipal ? notaDePromocion(pendientePrincipal) : null}
        confirmLabel="Marcar como principal"
        onConfirm={() => {
          const loc = pendientePrincipal;
          setPendientePrincipal(null);
          if (loc) aplicarPrincipal(loc);
        }}
        onCancel={() => setPendientePrincipal(null)}
      />

      <ConfirmDialog
        active={!!pendienteBorrado}
        title={`¿Borrar la ubicación «${pendienteBorrado?.name || ''}»?`}
        description="Su stock se devolverá al almacén principal."
        tone="danger"
        confirmLabel="Borrar ubicación"
        onConfirm={() => {
          const loc = pendienteBorrado;
          setPendienteBorrado(null);
          if (loc) borrarUbicacion(loc);
        }}
        onCancel={() => setPendienteBorrado(null)}
      />
    </div>
  );
};

// ── Diálogo de traspaso (con báscula si el producto es por peso) ──
const TransferDialog = ({ transfer, locations, token, venueID, onClose, onDone }) => {
  const { product, fromLoc } = transfer;
  const isWeight = product.unit_type === 'weight';
  const unit = isWeight ? 'g' : 'u';
  const step = isWeight ? '0.001' : '1';
  const available = Number(product.by_location?.[fromLoc.id] || 0);
  const fmt = (n) => Number(n || 0).toLocaleString('en-GB', { maximumFractionDigits: 3 });

  // Tara por ubicación: la de esa ubicación si existe, si no la del producto, si no ''.
  const tareForLoc = (locId) => {
    const t = product.tare_by_location?.[locId];
    if (t != null) return String(t);
    return product.tare_grams != null ? String(product.tare_grams) : '';
  };
  const sourceTara = Number(tareForLoc(fromLoc.id) || 0);

  const [toLoc, setToLoc] = useState(transfer.presetTo || '');
  const [qty, setQty] = useState('');
  const [gross, setGross] = useState('');
  const [taraInput, setTaraInput] = useState(transfer.presetTo ? tareForLoc(transfer.presetTo) : '');
  const [taraTouched, setTaraTouched] = useState(false);   // el usuario editó la tara a mano
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Al cambiar el destino, pre-rellena su tara guardada (o la del producto), salvo que
  // el usuario ya la haya editado a mano (entonces se respeta lo que escribió).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (!taraTouched) setTaraInput(toLoc ? tareForLoc(toLoc) : ''); }, [toLoc]);

  const taraVal = parseFloat(taraInput) || 0;
  const byWeight = isWeight && gross !== '';
  const net = byWeight ? (parseFloat(gross) - taraVal) : (qty !== '' ? parseFloat(qty) : null);

  const submit = async () => {
    setErr('');
    if (!toLoc) { setErr('Elige destino'); return; }
    if (net == null || isNaN(net) || net <= 0) { setErr('Cantidad no válida'); return; }
    if (net > available) { setErr(`Solo hay ${fmt(available)}${unit} en ${fromLoc.name}`); return; }
    setBusy(true);
    try {
      const body = { product_id: product.id, venue_id: venueID, from_location_id: fromLoc.id, to_location_id: toLoc, quantity: net };
      if (isWeight && taraInput !== '') body.dest_tare_grams = taraVal;   // guarda la tara del destino
      const res = await apiFetch('/v1/stock/transfer', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(venueID && { 'x-venue-id': venueID }) },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(apiErrorDetail(await safeJson(res), 'Error en el traspaso'));
      onDone();
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between border-b border-[var(--border)] p-4">
          <div>
            <h3 className="text-base font-semibold text-[var(--foreground)]">Traspasar · {product.name}</h3>
            <p className="mt-0.5 text-sm text-[var(--muted-foreground)]">
              Desde <strong>{fromLoc.name}</strong> · disponible {fmt(available)}{unit}
              {isWeight && <> · <span className="text-[var(--muted-foreground)]">tara origen {fmt(sourceTara)} g</span></>}
            </p>
          </div>
          <button onClick={onClose} className="rounded p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]">✕</button>
        </div>
        <div className="space-y-3 p-4">
          {err && <p className="rounded-md bg-red-50 px-3 py-2 text-sm text-[var(--destructive)]">{err}</p>}
          <div className="wa-form-field">
            <label className="wa-label">Destino</label>
            <select value={toLoc} onChange={(e) => setToLoc(e.target.value)} className="wa-input">
              <option value="">—</option>
              {locations.filter((l) => l.id !== fromLoc.id).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>

          <div className="wa-form-field">
            <label className="wa-label">Cantidad ({unit})</label>
            <input type="number" step={step} min="0" value={byWeight ? (net > 0 ? net : '') : qty}
              onChange={(e) => setQty(e.target.value)} className="wa-input" placeholder="0" disabled={byWeight} />
          </div>

          {isWeight && (
            <div className="space-y-2 rounded-lg border border-[var(--border)] p-3">
              <p className="text-xs font-medium text-[var(--muted-foreground)]">…o por peso del frasco</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="wa-form-field">
                  <label className="wa-label">Frasco en báscula (bruto, g)</label>
                  <input type="number" step={step} min="0" value={gross} onChange={(e) => setGross(e.target.value)} className="wa-input" placeholder="0.000" />
                </div>
                <div className="wa-form-field">
                  <label className="wa-label">Tara del frasco (g) · destino</label>
                  <input type="number" step={step} min="0" value={taraInput} onChange={(e) => { setTaraInput(e.target.value); setTaraTouched(true); }} className="wa-input" placeholder="0.000" />
                </div>
              </div>
              {gross !== '' && (
                <p className="rounded-sm bg-[var(--muted)] px-3 py-2 text-sm font-semibold text-[var(--foreground)]">
                  Neto a traspasar: {net != null && !isNaN(net) ? `${fmt(net)} g` : '—'}
                </p>
              )}
            </div>
          )}

          <Button onClick={submit} disabled={busy} className="w-full">
            {busy ? 'Traspasando…' : 'Traspasar'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ── Diálogo crear ubicación ──
const AddLocationDialog = ({ onClose, onCreate, permitirPrincipal = false }) => {
  const [name, setName] = useState('');
  const [kind, setKind] = useState('nevera');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="w-full max-w-sm rounded-2xl bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[var(--border)] p-4">
          <h3 className="text-base font-semibold text-[var(--foreground)]">Nueva ubicación</h3>
          <button onClick={onClose} className="rounded p-1 text-[var(--muted-foreground)] hover:bg-[var(--muted)]">✕</button>
        </div>
        <div className="space-y-3 p-4">
          <div className="wa-form-field">
            <label className="wa-label">Nombre</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="wa-input" placeholder="Ej: Nevera barra" />
          </div>
          <div className="wa-form-field">
            <label className="wa-label">Tipo</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="wa-input">
              <option value="nevera">Nevera</option>
              <option value="armario">Armario</option>
              <option value="sub_almacen">Sub-almacén</option>
              {permitirPrincipal && <option value="principal">Almacén principal</option>}
            </select>
            {permitirPrincipal && kind === 'principal' && (
              <p className="mt-1 text-xs text-[var(--muted-foreground)]">
                El género nuevo entrará aquí por defecto. Nace vacío: no se mueve nada de sitio.
              </p>
            )}
          </div>
          <Button onClick={() => name.trim() && onCreate(name.trim(), kind)} className="w-full">Crear</Button>
        </div>
      </div>
    </div>
  );
};

export default StockBoard;
